import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { CheckCircle, Mail } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import Button from "../../common/components/Button";
import { SPACING } from "../../common/constants/spacing";
import { useAuth } from "../../common/contexts/AuthContext";
import { resendVerification, verifyEmail } from "../api/auth";

const VerifyEmail = () => {
  const { t } = useTranslation("authentication");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email] = useState(searchParams.get("email") || "");
  const [code, setCode] = useState(searchParams.get("code") || "");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // Auto-verify if both email and code are in URL params
  useEffect(() => {
    if (email && code) {
      handleVerification();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleVerification = async () => {
    if (!email) {
      setError(t("verifyEmail.errors.emailMissing"));
      return;
    }

    if (!code) {
      setError(t("verifyEmail.errors.codeRequired"));
      return;
    }

    setLoading(true);
    setError("");
    setMessage("");

    try {
      const response = await verifyEmail(email, code);

      if (response.success) {
        setSuccess(true);
        setMessage(response.message);

        // Store tokens and set up authentication state
        if (response.access && response.refresh) {
          // Set flag to indicate this is a new user who should see theme selection
          localStorage.setItem("newUserThemeSelection", "true");

          // Store tokens first
          localStorage.setItem("accessToken", response.access);
          localStorage.setItem("refresh_token", response.refresh);

          // Use AuthContext login method to properly set authentication state
          const loginResult = await login({
            auto_login: true,
            access_token: response.access,
            is_new_user: true,
          });

          if (loginResult.success) {
            // Redirect to theme selection for new users after 2 seconds
            setTimeout(() => {
              navigate("/theme-selection");
            }, 2000);
          } else {
            // Fallback: redirect anyway since tokens are stored
            setTimeout(() => {
              navigate("/theme-selection");
            }, 2000);
          }
        }
      } else {
        setError(
          response.message || t("verifyEmail.errors.verificationFailed"),
        );
      }
    } catch (err) {
      setError(t("verifyEmail.errors.networkError"));
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      setError(t("verifyEmail.errors.emailRequired"));
      return;
    }

    setLoading(true);
    setError("");
    setMessage("");

    try {
      const response = await resendVerification(email);

      if (response.success) {
        setMessage(response.message);
      } else {
        setError(response.message || t("verifyEmail.errors.resendFailed"));
      }
    } catch (err) {
      setError(t("verifyEmail.errors.networkError"));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Card sx={{ maxWidth: 500, width: "100%", mx: 2 }}>
          <CardContent sx={{ textAlign: "center", p: 4 }}>
            <CheckCircle
              size={64}
              style={{
                color: "var(--mui-palette-success-main)",
                marginBottom: 16,
              }}
            />
            <Typography variant="h3" gutterBottom color="success.main">
              {t("verifyEmail.emailVerified")}
            </Typography>
            <Typography variant="bodyMd" color="text.secondary" mb={3}>
              {message} {t("verifyEmail.redirecting")}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
    >
      <Card sx={{ maxWidth: 500, width: "100%", mx: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box textAlign="center" mb={3}>
            <Mail
              size={48}
              style={{
                color: "var(--mui-palette-primary-main)",
                marginBottom: 16,
              }}
            />
            <Typography variant="h4" gutterBottom>
              {t("verifyEmail.title")}
            </Typography>
            <Typography variant="bodyMd" color="text.secondary" mb={1}>
              {t("verifyEmail.sentCodeTo")}{" "}
              <Typography
                variant="bodyMd"
                color="primary.main"
                fontWeight="medium"
              >
                {email}
              </Typography>
            </Typography>
            <br />
            <Typography variant="bodySm" color="text.secondary" mt={1}>
              {t("verifyEmail.enterCode")}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: SPACING.sm }}>
              {error}
            </Alert>
          )}

          {message && (
            <Alert severity="success" sx={{ mb: SPACING.sm }}>
              {message}
            </Alert>
          )}

          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              handleVerification();
            }}
          >
            <TextField
              fullWidth
              label={t("verifyEmail.verificationCode")}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              margin="normal"
              required
              disabled={loading}
              inputProps={{ maxLength: 6 }}
              placeholder={t("verifyEmail.codePlaceholder")}
              autoFocus
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 1, mb: SPACING.sm }}
              disabled={loading || !code}
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : (
                t("verifyEmail.verifyButton")
              )}
            </Button>

            <Box textAlign="center">
              <Typography variant="bodySm" color="text.secondary" mb={1}>
                {t("verifyEmail.didntReceive")}
              </Typography>
              <Button
                variant="text"
                onClick={handleResendVerification}
                disabled={loading}
              >
                {t("verifyEmail.resendButton")}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default VerifyEmail;
